import React from 'react';
import { Check, ChevronDown, ChevronUp, Wand2, RefreshCw, X, AlertCircle } from 'lucide-react';
import { FilterSkeleton } from './index';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './FilterSidebar.css';

const FilterCheckboxGroup = ({ title, filters, setFilters, scrollable = false, prices = {}, kms = {}, activeOptions = null, isSectionDisabled = false }) => {
    const handleSelectAll = () => {
        if (isSectionDisabled) return;
        const newFilters = {};
        Object.keys(filters).forEach(k => newFilters[k] = true);
        setFilters(newFilters);
    };

    const handleClearAll = () => {
        if (isSectionDisabled) return;
        const newFilters = {};
        Object.keys(filters).forEach(k => newFilters[k] = false);
        setFilters(newFilters);
    };

    return (
        <div className={`filter-section-container ${isSectionDisabled ? 'is-disabled' : ''}`} style={isSectionDisabled ? { opacity: 0.5 } : {}}>
            <div className="filter-section-header">
                <h3 className="filter-section-title">{title}</h3>
                <div className="filter-actions-group">
                    <button
                        onClick={handleSelectAll}
                        className="filter-action-link select-all"
                    >
                        Select All
                    </button>
                    <span className="filter-action-separator">|</span>
                    <button
                        onClick={handleClearAll}
                        className="filter-action-link clear-all"
                    >
                        Clear All
                    </button>
                </div>
            </div>

            <div className={`filter-checkbox-group ${scrollable ? 'max-h-48 overflow-y-auto pr-2 custom-scrollbar' : ''}`}>
                {Object.entries(filters).map(([key, value]) => {
                    const isRelevant = activeOptions === null || activeOptions[key];
                    if (!isRelevant) return null; // Hide irrelevant options completely for a cleaner mode-specific UI

                    const isDisabled = isSectionDisabled;
                    return (
                        <label key={key} className="filter-checkbox-label" style={{ width: '100%', justifyContent: 'space-between', opacity: isDisabled ? 0.6 : 1, gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                                <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={value}
                                    onChange={() => setFilters({ ...filters, [key]: !value })}
                                    disabled={isDisabled}
                                />
                                <div className={`filter-checkbox-box ${value ? 'checked' : ''}`}>
                                    {value && <Check size={14} className="text-white" strokeWidth={3} />}
                                </div>
                                <span className="filter-checkbox-text" title={key} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{key}</span>
                            </div>
                            {kms[key] > 0 && (
                                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '500', marginLeft: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    {kms[key]} km
                                </span>
                            )}
                            {prices[key] !== undefined && prices[key] !== Infinity && (
                                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '500', marginLeft: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    ₹{(prices[key]).toLocaleString('en-IN')}
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>
            <div className="filter-section-divider"></div>
        </div>
    );
};

const RangeSlider = ({ min, max, value, onChange, formatLabel = (v) => v, disabled = false }) => {
    // rc-slider expects an array for range: [min, max]
    const rangeValue = [value.min, value.max];

    const handleSliderChange = (newValues) => {
        onChange({ min: newValues[0], max: newValues[1] });
    };

    return (
        <div className="range-slider-container">
            <div className="sliders-control">
                <Slider
                    range
                    min={min}
                    max={max}
                    value={rangeValue}
                    onChange={handleSliderChange}
                    allowCross={false}
                    disabled={disabled}
                    className="rc-custom-slider"
                />
            </div>
            <div className="slider-values">
                <span>{formatLabel(value.min)}</span>
                <span>{formatLabel(value.max)}</span>
            </div>
        </div>
    );
};

const SmartFilter = ({ context, onApply, onReset, isDisabled, isMobile, onTabChange, isDataZero, selectedSort }) => {
    const [query, setQuery] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [activeChips, setActiveChips] = React.useState([]); // To show objects like { type: 'fare', label: 'Fare < 7000' }

    const handleChipRemove = (idx, type) => {
        setActiveChips(prev => prev.filter((_, i) => i !== idx));
        if (onReset) onReset(type);
    };

    const handleSubmit = async () => {
        if (!query.trim() || isLoading) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('https://r6y1hkvcyk.execute-api.ap-south-1.amazonaws.com/prod/api/getFilters', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: query.trim(),
                    context
                })
            });

            if (!response.ok) throw new Error('API Error');

            const data = await response.json();

            if (data.filters) {
                onApply(data.filters);

                // Construct chips from response
                const newChips = [];
                const f = data.filters;
                if (f.departure_time?.start || f.departure_time?.end) {
                    newChips.push({
                        type: 'time',
                        label: `Time: ${f.departure_time.start || '00:00'} - ${f.departure_time.end || '23:59'}`
                    });
                }
                if (f.duration?.max_minutes) {
                    const h = Math.floor(f.duration.max_minutes / 60);
                    const m = f.duration.max_minutes % 60;
                    newChips.push({
                        type: 'duration',
                        label: `Duration < ${h}h ${m}m`
                    });
                }
                if (f.fare?.max || f.fare?.min) {
                    const label = (f.fare.min && f.fare.max)
                        ? `Fare: ₹${f.fare.min.toLocaleString()} - ₹${f.fare.max.toLocaleString()}`
                        : (f.fare.max ? `Fare < ₹${f.fare.max.toLocaleString()}` : `Fare > ₹${f.fare.min.toLocaleString()}`);
                    newChips.push({ type: 'fare', label });
                }
                const formatLabel = (str) => str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                if (f.mode) {
                    newChips.push({ type: 'mode', label: `Mode: ${formatLabel(f.mode)}` });
                }
                if (f.non_stop) {
                    newChips.push({ type: 'non_stop', label: `Non-stop only` });
                }
                if (f.only_full_availability) {
                    newChips.push({ type: 'availability', label: `Fully Available Only` });
                }
                if (f.operators && f.operators.length > 0) {
                    newChips.push({ type: 'operators', label: `Operator: ${f.operators.join(', ')}` });
                }
                if (f.sort) {
                    newChips.push({ type: 'sort', label: `Sort: ${formatLabel(f.sort)}` });
                }
                setActiveChips(newChips);
                setQuery(''); // Clear on success
            } else {
                setError('Something went wrong');
            }
        } catch (err) {
            console.error('Smart Filter Error:', err);
            setError('Something went wrong');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearAll = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setQuery('');
        setActiveChips([]);
        setError(null);
        if (onReset) onReset('all'); // Trigger tab revert and filter reset
    };

    return (
        <div className={`smart-filter-widget ${isCollapsed ? 'collapsed' : ''} ${isDisabled ? 'disabled' : ''}`}>
            <div className="smart-filter-header" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="smart-filter-title-group">
                    <div className="smart-filter-icon-bg">
                        <Wand2 size={16} className="smart-filter-wand" />
                    </div>
                    <div className="smart-filter-text-stack">
                        <h3 className="smart-filter-title">AI Filters</h3>
                    </div>
                    <span className="smart-filter-new-badge">Beta</span>
                </div>
                <div className="smart-filter-toggle">
                    {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                </div>
            </div>

            {!isCollapsed && (
                <div className="smart-filter-body">
                    {/* Error Message Section - Shown above chips when results are zero */}
                    {isDataZero && activeChips.length > 0 && (
                        <div className="smart-filter-error-box">
                            <AlertCircle size={16} className="smart-filter-error-icon" />
                            <span className="smart-filter-error-text">
                                No routes found for selected filters.
                                <span className="smart-filter-error-reset" onClick={(e) => handleClearAll(e)}>Reset filters</span>
                            </span>
                        </div>
                    )}

                    {activeChips.length > 0 && (
                        <div className="smart-filter-chips-container">
                            {activeChips.map((chip, idx) => (
                                <div key={idx} className="smart-filter-chip">
                                    <span>{chip.label}</span>
                                    <X size={12} onClick={() => handleChipRemove(idx, chip.type)} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="smart-filter-input-area">
                        <textarea
                            className="smart-filter-textarea"
                            placeholder="flights after 11am and price below less than 10k, only layover of 1 hr"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            disabled={isLoading || isDisabled}
                        />
                        <div className="smart-filter-input-footer">
                            <button className="smart-filter-clear-link" onClick={handleClearAll} disabled={isLoading}>
                                Clear filters
                            </button>
                            {error && <p className="smart-filter-error-msg">{error}</p>}
                        </div>
                    </div>

                    <button
                        className={`smart-filter-submit-btn-primary ${isLoading ? 'loading' : ''}`}
                        onClick={handleSubmit}
                        disabled={isLoading || (!query.trim() && activeChips.length === 0) || isDisabled}
                    >
                        {isLoading ? (
                            <>
                                <RefreshCw size={16} className="animate-spin" />
                                Processing...
                            </>
                        ) : (
                            'Apply AI Filters'
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

const FilterSidebar = ({
    stopsFilter,
    setStopsFilter,
    trainsFilter,
    setTrainsFilter,
    sourcesFilter,
    setSourcesFilter,
    destinationsFilter,
    setDestinationsFilter,
    viaFilter = {},
    setViaFilter = () => { },
    operatorPrices = {},
    sourceKms = {},
    destinationKms = {},
    activeOperatorOptions = null,
    activeSourceOptions = null,
    activeDestinationOptions = null,
    activeStopOptions = null,
    activeViaOptions = null,
    departureTimeFilter, // Expecting { min, max }
    setDepartureTimeFilter,
    durationFilter,      // Expecting { min, max }
    setDurationFilter,
    fareFilter,          // Expecting { min, max }
    setFareFilter,
    maxDeparture = 24,
    maxDuration = 0,
    maxFare = 0,
    isLoading = false,
    hideCancelled = false,
    setHideCancelled = () => { },
    showHideCancelled = false,
    showFullyAvailable = true,
    setShowFullyAvailable = () => { },
    avoidStationChange = false,
    setAvoidStationChange = () => { },
    showAvoidStationChange = false,
    // Accordion props for mobile
    isMobile = false,
    isOpen = false,
    onToggle = () => { },
    filteredResultsCount = -1, // Default to -1 so it doesn't trigger unexpectedly
    isDisabled = false,
    onTabChange = () => { },
    resultsMode = 'All',
    selectedSort = 'Best',
    setSelectedSort = () => { },
    showAiFilter = true // Default to true for stability
}) => {
    const [initialModeBeforeAI, setInitialModeBeforeAI] = React.useState(null);

    // Only show skeleton if explicitly loading AND we have no filter data yet
    const hasFilters = Object.keys(stopsFilter).length > 0 ||
        Object.keys(trainsFilter).length > 0 ||
        Object.keys(sourcesFilter).length > 0;

    if (isLoading && !hasFilters) {
        // On desktop show skeleton, on mobile the main render will handle it (SmartFilter stays visible)
        if (!isMobile) return <FilterSkeleton />;
    }

    // Helper to format minutes of the day into HH:mm
    const formatTime = (totalMinutes) => {
        const hour = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        return `${String(hour).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };

    // New logic: Check if data is not found and which filters are causing it
    const isDataZero = !isLoading && filteredResultsCount === 0;

    // Define which sections are currently restricting results (Active)
    const isStopsActive = Object.values(stopsFilter || {}).some(v => !v);
    const isTrainsActive = Object.values(trainsFilter || {}).some(v => !v);
    const isSourcesActive = Object.values(sourcesFilter || {}).some(v => !v);
    const isDestinationsActive = Object.values(destinationsFilter || {}).some(v => !v);
    const isViaActive = Object.values(viaFilter || {}).some(v => !v);

    // Filter Sliders: Active if they are not at their full range
    const isDepartureActive = departureTimeFilter.min > 0 || (maxDeparture > 0 && departureTimeFilter.max < maxDeparture);
    const isDurationActive = durationFilter.min > 0 || (maxDuration > 0 && durationFilter.max < maxDuration);
    const isFareActive = fareFilter.min > 0 || (maxFare > 0 && fareFilter.max < maxFare);

    // Boolean Checkboxes: Active if they are TRUE (since default is FALSE)
    const isAvailabilityActive = showFullyAvailable === true;
    const isAvoidActive = avoidStationChange === true;
    const isCancelledActive = hideCancelled === true;

    // A section should ONLY be disabled if:
    // 1. Results are zero 
    // AND 2. The section itself is NOT active (user hasn't touched it)
    // This allows users to undo the filter that caused zero results.
    const shouldDisable = (isActive) => isDataZero && !isActive;

    return (
        <aside
            className={`filter-sidebar-main ${isMobile ? (isOpen ? 'open' : 'closed') : ''} ${isDisabled ? 'sidebar-locked' : ''}`}
            style={isDisabled ? { opacity: 0.8 } : {}}
        >
            <div className="filter-sidebar-inner">
                <div
                    className={`filter-header-container ${isMobile ? 'cursor-pointer mobile-toggle' : ''}`}
                    onClick={isMobile ? onToggle : undefined}
                >
                    <div className="filter-header-flex-row">
                        <div className="filter-title-stack">
                            <h2 className="filter-main-title">Filter</h2>
                            <p className="filter-subtitle">Use the filters to get tailored results</p>
                        </div>
                        {isMobile && (
                            <div className="filter-mobile-arrow">
                                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        )}
                    </div>
                </div>

                <div className={`filter-accordion-content ${isMobile ? (isOpen ? 'open' : 'closed') : 'desktop'}`}>
                    <div className="filter-content-wrapper">
                        {showAiFilter && (
                            <SmartFilter
                                isMobile={isMobile}
                                isDisabled={isDisabled}
                                isDataZero={isDataZero}
                                onTabChange={onTabChange}
                                selectedSort={selectedSort}
                                onReset={(type) => {
                                    if (type === 'fare' || type === 'all') setFareFilter({ min: 0, max: maxFare });
                                    if (type === 'duration' || type === 'all') setDurationFilter({ min: 0, max: maxDuration });
                                    if (type === 'time' || type === 'all') setDepartureTimeFilter({ min: 0, max: maxDeparture });
                                    if (type === 'availability' || type === 'all') setShowFullyAvailable(false);
                                    if (type === 'sort' || type === 'all') setSelectedSort('Best');
                                    if (type === 'non_stop' || type === 'all') {
                                        const allStops = {};
                                        Object.keys(stopsFilter).forEach(k => allStops[k] = true);
                                        setStopsFilter(allStops);
                                    }
                                    if (type === 'operators' || type === 'all') {
                                        const allOps = {};
                                        Object.keys(trainsFilter).forEach(k => allOps[k] = true);
                                        setTrainsFilter(allOps);
                                    }
                                    if (type === 'sources' || type === 'all') {
                                        const allSources = {};
                                        Object.keys(sourcesFilter).forEach(k => allSources[k] = true);
                                        setSourcesFilter(allSources);
                                    }
                                    if (type === 'destinations' || type === 'all') {
                                        const allDests = {};
                                        Object.keys(destinationsFilter).forEach(k => allDests[k] = true);
                                        setDestinationsFilter(allDests);
                                    }
                                    if (type === 'via' || type === 'all') {
                                        const allVia = {};
                                        Object.keys(viaFilter).forEach(k => allVia[k] = true);
                                        setViaFilter(allVia);
                                    }
                                    if (type === 'avoid' || type === 'all') setAvoidStationChange(false);
                                    if (type === 'mode' || type === 'all') {
                                        if (initialModeBeforeAI) {
                                            onTabChange(initialModeBeforeAI);
                                            setInitialModeBeforeAI(null);
                                        }
                                    }
                                }}
                                context={{
                                    available_operators: Object.keys(trainsFilter),
                                    departure_time_range: { min: "00:00", max: "23:59" },
                                    fare_range: { min: 0, max: maxFare }
                                }}
                                onApply={(filters) => {
                                    if (!initialModeBeforeAI) setInitialModeBeforeAI(resultsMode);
                                    setFareFilter({ min: 0, max: maxFare });
                                    setDurationFilter({ min: 0, max: maxDuration });
                                    setDepartureTimeFilter({ min: 0, max: maxDeparture });
                                    setAvoidStationChange(false);
                                    const resetCheckboxes = (currentFilter, setter) => {
                                        const allChecked = {};
                                        Object.keys(currentFilter).forEach(k => allChecked[k] = true);
                                        setter(allChecked);
                                    };
                                    resetCheckboxes(stopsFilter, setStopsFilter);
                                    resetCheckboxes(trainsFilter, setTrainsFilter);
                                    resetCheckboxes(sourcesFilter, setSourcesFilter);
                                    resetCheckboxes(destinationsFilter, setDestinationsFilter);
                                    resetCheckboxes(viaFilter, setViaFilter);

                                    if (filters.operators) {
                                        const newTrains = { ...trainsFilter };
                                        Object.keys(newTrains).forEach(k => newTrains[k] = false);
                                        filters.operators.forEach(op => {
                                            if (newTrains.hasOwnProperty(op)) newTrains[op] = true;
                                        });
                                        setTrainsFilter(newTrains);
                                    }
                                    if (filters.non_stop !== undefined) {
                                        const newStops = { ...stopsFilter };
                                        Object.keys(newStops).forEach(k => {
                                            newStops[k] = filters.non_stop ? (k === '0' || k.toLowerCase().includes('non')) : true;
                                        });
                                        setStopsFilter(newStops);
                                    }
                                    if (filters.departure_time) {
                                        const parseTimeToMinutes = (timeStr) => {
                                            const [h, m] = timeStr.split(':').map(Number);
                                            return h * 60 + m;
                                        };
                                        const newRange = { ...departureTimeFilter };
                                        if (filters.departure_time.start) newRange.min = parseTimeToMinutes(filters.departure_time.start);
                                        if (filters.departure_time.end) newRange.max = parseTimeToMinutes(filters.departure_time.end);
                                        setDepartureTimeFilter(newRange);
                                    }
                                    if (filters.fare) {
                                        setFareFilter({ min: filters.fare.min || 0, max: filters.fare.max || maxFare });
                                    }
                                    if (filters.mode) {
                                        const modeMap = { train: 'Train', flight: 'Flight', all: 'All', mixed: 'Mixed' };
                                        const targetMode = modeMap[filters.mode.toLowerCase()];
                                        if (targetMode) onTabChange(targetMode);
                                    }

                                    if (filters.only_full_availability !== undefined) {
                                        setShowFullyAvailable(!!filters.only_full_availability);
                                    }

                                    if (filters.sort) {
                                        const sortMap = {
                                            'best': 'Best',
                                            'cheapest': 'Cheapest Available',
                                            'quickest': 'Quickest Available',
                                            'cheapest_available': 'Cheapest Available',
                                            'quickest_available': 'Quickest Available',
                                            'early_arrival': 'Early Arrival',
                                            'early_departure': 'Early Departure',
                                            'late_departure': 'Late Departure',
                                            'reach_closest_to_destination': 'Closest Destination'
                                        };
                                        const targetSort = sortMap[filters.sort.toLowerCase()];
                                        if (targetSort) setSelectedSort(targetSort);
                                    }
                                }}
                            />
                        )}

                        <FilterCheckboxGroup
                            title="Stops"
                            filters={stopsFilter}
                            setFilters={setStopsFilter}
                            isSectionDisabled={shouldDisable(isStopsActive)}
                            activeOptions={activeStopOptions}
                        />

                        <FilterCheckboxGroup
                            title="Operators"
                            filters={trainsFilter}
                            setFilters={setTrainsFilter}
                            scrollable={true}
                            prices={operatorPrices}
                            isSectionDisabled={shouldDisable(isTrainsActive)}
                            activeOptions={activeOperatorOptions}
                        />

                        <FilterCheckboxGroup
                            title="Sources"
                            filters={sourcesFilter}
                            setFilters={setSourcesFilter}
                            scrollable={true}
                            kms={sourceKms}
                            isSectionDisabled={shouldDisable(isSourcesActive)}
                            activeOptions={activeSourceOptions}
                        />

                        <FilterCheckboxGroup
                            title="Destinations"
                            filters={destinationsFilter}
                            setFilters={setDestinationsFilter}
                            scrollable={true}
                            kms={destinationKms}
                            isSectionDisabled={shouldDisable(isDestinationsActive)}
                            activeOptions={activeDestinationOptions}
                        />
                        {Object.keys(viaFilter).length > 0 && (
                            <FilterCheckboxGroup
                                title="Via"
                                filters={viaFilter}
                                setFilters={setViaFilter}
                                scrollable={true}
                                activeOptions={activeViaOptions}
                                isSectionDisabled={shouldDisable(isViaActive)}
                            />
                        )}

                        <div className="filter-section-container" style={shouldDisable(isAvailabilityActive) ? { opacity: 0.5 } : {}}>
                            <div className="filter-section-header">
                                <h3 className="filter-section-title">Availability</h3>
                            </div>
                            <div className="filter-checkbox-group">
                                <label className="filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={showFullyAvailable}
                                        onChange={() => setShowFullyAvailable(!showFullyAvailable)}
                                        disabled={shouldDisable(isAvailabilityActive)}
                                    />
                                    <div className={`filter-checkbox-box ${showFullyAvailable ? 'checked' : ''}`}>
                                        {showFullyAvailable && <Check size={14} className="text-white" strokeWidth={3} />}
                                    </div>
                                    <span className="filter-checkbox-text">Show fully available routes</span>
                                </label>
                                {showHideCancelled && (
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={hideCancelled}
                                            onChange={() => setHideCancelled(!hideCancelled)}
                                            disabled={shouldDisable(isCancelledActive)}
                                        />
                                        <div className={`filter-checkbox-box ${hideCancelled ? 'checked' : ''}`}>
                                            {hideCancelled && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                        <span className="filter-checkbox-text">Hide Cancelled Trains</span>
                                    </label>
                                )}
                            </div>
                            <div className="filter-section-divider"></div>
                        </div>

                        {showAvoidStationChange && (
                            <div className="filter-section-container" style={shouldDisable(isAvoidActive) ? { opacity: 0.5 } : {}}>
                                <div className="filter-section-header">
                                    <h3 className="filter-section-title">Transfer Preferences</h3>
                                </div>
                                <div className="filter-checkbox-group">
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={avoidStationChange}
                                            onChange={() => setAvoidStationChange(!avoidStationChange)}
                                            disabled={shouldDisable(isAvoidActive)}
                                        />
                                        <div className={`filter-checkbox-box ${avoidStationChange ? 'checked' : ''}`}>
                                            {avoidStationChange && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                        <span className="filter-checkbox-text">Avoid Station/Airport Change</span>
                                    </label>
                                </div>
                                <div className="filter-section-divider"></div>
                            </div>
                        )}

                        <div className="filter-range-container" style={shouldDisable(isDepartureActive) ? { opacity: 0.5 } : {}}>
                            <h3 className="filter-range-title">Departure Time</h3>
                            <RangeSlider
                                min={0}
                                max={maxDeparture}
                                value={departureTimeFilter}
                                onChange={setDepartureTimeFilter}
                                formatLabel={formatTime}
                                disabled={shouldDisable(isDepartureActive)}
                            />
                            <div className="filter-section-divider" style={{ marginTop: '0' }}></div>
                        </div>

                        <div className="filter-range-container" style={shouldDisable(isDurationActive) ? { opacity: 0.5 } : {}}>
                            <h3 className="filter-range-title">Duration (Hrs)</h3>
                            <RangeSlider
                                min={0}
                                max={maxDuration}
                                value={durationFilter}
                                onChange={setDurationFilter}
                                disabled={shouldDisable(isDurationActive)}
                            />
                            <div className="filter-section-divider" style={{ marginTop: '0' }}></div>
                        </div>

                        <div className="filter-range-container" style={shouldDisable(isFareActive) ? { opacity: 0.5 } : {}}>
                            <h3 className="filter-range-title">Fare (INR)</h3>
                            <RangeSlider
                                min={0}
                                max={maxFare}
                                value={fareFilter}
                                onChange={setFareFilter}
                                formatLabel={(v) => `₹${v.toLocaleString()}`}
                                disabled={shouldDisable(isFareActive)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    );
};


export default FilterSidebar;
